import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getAccessToken } from '@/utils/jwt';

export interface ReviewRequest {
  id: string;
  project_id: string | null;
  question: string;
  agent_answer: string;
  conflicting_sources: Record<string, unknown>[] | null;
  confidence_score: number;
  routed_to: string | null;
  status: 'pending' | 'confirmed' | 'corrected' | 'dismissed';
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ReviewFeedback {
  id: string;
  review_request_id: string;
  responder_id: string;
  action: 'confirm' | 'correct' | 'pin' | 'dismiss';
  correction_text: string | null;
  created_at: string;
}

export interface ReviewWithFeedback extends ReviewRequest {
  feedback: ReviewFeedback[];
}

export interface ListReviewsResponse {
  success: boolean;
  reviews: ReviewRequest[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListReviewsParams {
  status?: string;
  routed_to?: string;
  project_id?: string;
  limit?: number;
  offset?: number;
}

export interface RespondToReviewParams {
  id: string;
  action: 'confirm' | 'correct' | 'pin' | 'dismiss';
  correction_text?: string;
}

export const reviewApi = createApi({
  reducerPath: 'reviewApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Review'],
  endpoints: (builder) => ({
    listReviews: builder.query<
      { reviews: ReviewRequest[]; total: number },
      ListReviewsParams | undefined
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.status) searchParams.set('status', params.status);
        if (params?.routed_to) searchParams.set('routed_to', params.routed_to);
        if (params?.project_id) searchParams.set('project_id', params.project_id);
        if (params?.limit) searchParams.set('limit', String(params.limit));
        if (params?.offset) searchParams.set('offset', String(params.offset));
        const qs = searchParams.toString();
        return `/knowledge/reviews${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (response: ListReviewsResponse) => ({
        reviews: response.reviews,
        total: response.total,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.reviews.map(({ id }) => ({ type: 'Review' as const, id })),
              { type: 'Review' as const, id: 'LIST' },
            ]
          : [{ type: 'Review' as const, id: 'LIST' }],
    }),
    getReview: builder.query<ReviewWithFeedback, string>({
      query: (id) => `/knowledge/reviews/${id}`,
      transformResponse: (response: { success: boolean; review: ReviewWithFeedback }) =>
        response.review,
      providesTags: (_result, _error, id) => [{ type: 'Review', id }],
    }),
    respondToReview: builder.mutation<
      { success: boolean; already_resolved: boolean; action: string },
      RespondToReviewParams
    >({
      query: ({ id, ...body }) => ({
        url: `/knowledge/reviews/${id}/respond`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Review', id },
        { type: 'Review', id: 'LIST' },
      ],
    }),
  }),
});

export const { useListReviewsQuery, useGetReviewQuery, useRespondToReviewMutation } = reviewApi;
